import { ArrowLeft, PanelRightOpen } from "lucide-react";
import type React from "react";
import { useRef } from "react";
import { Button, TooltipTrigger } from "react-aria-components";
import { Link, NavLink, useNavigate } from "react-router";
import { cn } from "~/utils/cn";
import { Tooltip } from "./Tooltip";

type NavItemProps = {
  icon: React.FC<{ className?: string }>;
  label: string;
  count?: number;
};

type NavbarLinkProps = NavItemProps & {
  to: string;
  end?: boolean;
};

export function NavBar({ children }: { children: React.ReactNode }) {
  // Map over children to inject the prop into NavBarHeader
  return (
    <nav className="bg-dark-900 body-md-500 collapsed:w-18 sticky top-0 flex h-screen w-60 flex-col p-[1rem] transition-[width]">
      {children}
    </nav>
  );
}

export function NavBarHeader({
  onCollapse,
  children,
}: {
  onCollapse: () => void;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-[0.5rem] border-b border-white/20">
      <div className="collapsed:justify-center flex items-center justify-between py-[0.5rem]">
        <img
          className="collapsed:hidden h-5"
          src="/icons/logo-with-name.svg#logo"
          alt="Logo"
        />
        <Button
          className="collapsed:rotate-180 rounded p-0.5 text-white opacity-60 transition-transform hover:opacity-80"
          onPress={onCollapse}
        >
          <PanelRightOpen className="size-5" />
        </Button>
      </div>
      {children}
    </div>
  );
}

export function NavBarLinks({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex shrink grow flex-col gap-0.5 overflow-x-hidden overflow-y-auto py-[0.75rem]">
      {children}
    </div>
  );
}

export function NavItem({ icon: Icon, label, count }: NavItemProps) {
  return (
    <div className="group flex cursor-pointer items-center justify-between">
      <div className="flex items-center gap-[0.625rem] text-white/80 group-hover:text-white group-[.active]:text-white">
        <div className="rounded-md p-[0.5rem] group-hover:bg-white/10 group-[.active]:bg-white/20 hover:bg-white/20">
          <Icon className="size-5" />
        </div>
        <div className="collapsed:opacity-0 overflow-hidden py-[0.5rem] whitespace-nowrap">
          {label}
        </div>
      </div>
      {count ? <CountBadge isActive>{count}</CountBadge> : null}
    </div>
  );
}

export function NavbarLink({ to, end, ...navItemProps }: NavbarLinkProps) {
  return (
    <NavLink className="group" to={to} end={end}>
      <NavItem {...navItemProps} />
    </NavLink>
  );
}

export function CountBadge(props: {
  isActive?: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        `text-dark-600 body-sm-600 collapsed:hidden flex h-[1rem] min-w-[1.5rem] items-center justify-center rounded px-1 text-[0.625rem]`,
        props.isActive ? "bg-brand-primary" : "bg-dark-100",
        props.className
      )}
    >
      {props.children}
    </span>
  );
}

export function NavBarFooter({ children }: { children: React.ReactNode }) {
  return <div className="border-t border-white/20 pt-[1rem]">{children}</div>;
}

export function TopNavBar({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "border-outline-100 flex h-12 shrink-0 grow-0 flex-row items-center justify-between gap-2 border-b px-10 py-3",
        className
      )}
    >
      {children}
    </div>
  );
}

export function PageTitle({
  className,
  children,
  backTo,
  backToTooltip,
}: {
  className?: string;
  children: React.ReactNode;
  backTo?: string;
  backToTooltip?: string;
}) {
  const navigate = useNavigate();
  return (
    <div
      className={cn(
        "inline-flex w-full items-center gap-2 overflow-y-hidden py-1"
      )}
    >
      {backTo ? (
        <TooltipTrigger delay={100} closeDelay={100}>
          <Button onPress={() => navigate(backTo)} className="cursor-pointer">
            <ArrowLeft className="text-dark-600 size-4 stroke-2" />
          </Button>
          {backToTooltip ? (
            <Tooltip placement="bottom">{backToTooltip}</Tooltip>
          ) : null}
        </TooltipTrigger>
      ) : null}
      <div
        className={cn(
          "body-lg-500 flex w-full items-center gap-2 truncate",
          className
        )}
      >
        {children}
      </div>
    </div>
  );
}

// We have a set of random images for the background of the anonymous navigation bar. Numbered 1 to 13.
const imageBase =
  "https://storage.googleapis.com/vauldron-static/img/anon-nav-bar/";
const randomImage = () =>
  imageBase + Math.floor(Math.random() * 13 + 1) + ".png";

export function AnonNavBar({ className }: { className?: string }) {
  // Generate a random image and position it randomly within the bounds of the navbar.
  // Use a ref to avoid re-generating on every render.
  const { image, style } = useRef({
    image: randomImage(),
    style: {
      top: `${Math.random() * 30}%`,
      right: "-100px",
    },
  }).current;

  return (
    <div
      className={cn(`hidden h-screen w-60 shrink-0 grow-0 md:block`, className)}
    >
      <div className="bg-dark-900 relative h-full w-full overflow-clip">
        <img
          src={image}
          alt="Background"
          className="absolute min-w-[800px] object-cover"
          style={style}
        />
        <Link to="https://www.valdera.com">
          <img
            src="/icons/logo-with-name.svg#logo"
            alt="Logo"
            className="absolute top-0 left-0 z-10 mx-auto w-[10rem] p-6"
          />
        </Link>
      </div>
    </div>
  );
}
